/**
 * Vue directive for auto-scrolling to the bottom of an element
 * Usage: v-auto-scroll or v-auto-scroll="true"
 * 
 * Options:
 * - delay: the delay in milliseconds before the scrolling starts
 * - childrenVisibilityDuration: the duration in milliseconds that one children need to be visible
 * - scrollMode: the mode of the scroll, can be 'infinite', 'one-way' or 'bidirectional'
 * 
 * The directive will automatically scroll the element to the bottom when the element is mounted
 */

export default {
  name: 'auto-scroll',
  
  mounted(el, binding) {
    const DEFAULT_DELAY = 0;
    const DEFAULT_CHILDREN_VISIBILITY_DURATION = 1000;
    const DEFAULT_SCROLL_MODE = 'infinite';

    const options = {
      smooth: true,
      delay: binding.value?.delay
        || DEFAULT_DELAY,
      childrenVisibilityDuration: binding.value?.childrenVisibilityDuration
        || DEFAULT_CHILDREN_VISIBILITY_DURATION,
      scrollMode: binding.value?.scrollMode
        || DEFAULT_SCROLL_MODE,
    };

    options.duration = getSpeedByChildrenVisibilityDuration(el, options.childrenVisibilityDuration);
    
    el._autoScrollOptions = options;

    setTimeout(() => {
      el._scrollCompleted = 0;

      animateScroll(el, options);
    }, options.delay);
  },
  
  unmounted(el) {
    delete el._autoScrollOptions;
  }
};

function getSpeedByChildrenVisibilityDuration(el, itemsVisibilityDuration) {
  return itemsVisibilityDuration * el.children.length;
}

/**
 * Animates the scroll of the element
 * @param {HTMLElement} el - The element to scroll
 * @param {Object} options - The options for the scroll
 * @param {string} direction - The direction to scroll
 */
function animateScroll(el, options, direction = 'bottom') {
  if (!el) return;

  const start = el.scrollTop;
  const target = 
    direction === 'bottom' ?
    el.scrollHeight - el.clientHeight :
    0;
  const distance = target - start;
  
  if (distance === 0) return;
  
  const startTime = performance.now();

  let numberOfScrolls;

  switch (options.scrollMode) {
    case 'bidirectional':
      numberOfScrolls = 2;
      break;
    case 'one-way':
      numberOfScrolls = 1;
      break;
    case 'infinite':
      numberOfScrolls = Infinity;
      break;
    default:
      numberOfScrolls = 1;
      break;
  }
  
  function animate(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / options.duration, 1);
    
    el.scrollTop = start + (distance * progress);
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      el._scrollCompleted = el._scrollCompleted + 1;

      // If the scroll is not complete, animate the next scroll
      // to the opposite direction
      if (el._scrollCompleted !== numberOfScrolls) {
        const nextDirection = direction === 'bottom' ? 'top' : 'bottom';

        animateScroll(el, options, nextDirection);
      } else {
        // If the scroll is complete, emit the scroll-complete event
        el.dispatchEvent(new CustomEvent('scroll-complete'));
        el._scrollCompleted = 0;
      }
    }
  }
  
  requestAnimationFrame(animate);
}
